import React, { Fragment, useEffect, useRef, useState } from 'react';
import classes from './ActionMenu.module.scss';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { ReactComponent as PlusIcon } from 'new/components/icons/fi-sr-plus-small.svg';
import classNames from 'classnames';

interface ActionMenuProps {
  wrapPosition?: boolean; // Wraps the container with a div that can be used inside a relative positioned element
  children:
    | React.ReactNode
    | ((options: { onCloseOptions: (event?: any, reason?: any) => void }) => React.ReactNode);
  pulse?: boolean;
  onCloseOptions?: (event?: any, reason?: any) => void;
  onMenuStateChange?: (isOpen: boolean) => void;
}

interface ActionItemProps {
  setAnchor?: (anchor: any) => void;
  onClick?: () => void;
  className?: string;
  icon?: React.ReactNode;
  children: string | React.ReactNode;
  onCloseOptions?: (event?: any, reason?: any) => void;
}

function isActionItem(element: any): element is React.ComponentType<ActionItemProps> {
  return element === ActionItem;
}

const wrapStyle: React.CSSProperties = {
  position: 'absolute' as const,
  right: 16,
  height: 24,
  width: 24,
};

export const ActionItem = (props: ActionItemProps) => {
  const { onClick, setAnchor, icon, children, className } = props;

  const onLocalClick = (event) => {
    setAnchor?.(null);
    onClick();
    props.onCloseOptions?.(event, 'click');
  };

  return (
    <MenuItem
      data-testid={props['data-testid']}
      className={classNames(classes.actionMenuItem, className)}
      onClick={onLocalClick}
    >
      {!!icon && <ListItemIcon>{icon}</ListItemIcon>}
      <ListItemText>{children}</ListItemText>
    </MenuItem>
  );
};

export const ActionMenu = (props: ActionMenuProps) => {
  const { children, pulse, wrapPosition, onMenuStateChange } = props;
  const [anchor, setAnchor] = useState(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [layoutDir, setLayoutDir] = useState('LR');

  useEffect(() => {
    if (!anchor) return;
    const dir =
      containerRef.current
        ?.closest('[data-layout-direction]')
        ?.getAttribute('data-layout-direction') ?? 'LR';
    setLayoutDir(dir);
  }, [anchor]);

  useEffect(() => {
    if (onMenuStateChange) {
      onMenuStateChange(!!anchor);
    }
  }, [anchor, onMenuStateChange]);

  const onClickOptions = (e) => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
  };

  const onCloseOptions = (event, reason) => {
    if (reason === 'escapeKeyDown' || reason === 'tabKeyDown') {
      return;
    }
    setAnchor(null);
  };

  const childrenWithProps =
    typeof children !== 'function'
      ? React.Children.map(children, (child) => {
          if (React.isValidElement(child) && isActionItem(child.type)) {
            return React.cloneElement(child as React.ReactElement<ActionItemProps>, {
              setAnchor,
              onCloseOptions,
            });
          }
          return child;
        })
      : null;

  const Container = wrapPosition ? 'div' : Fragment;
  const containerProps = Container === 'div' ? { style: wrapStyle } : {};

  return (
    <Container {...containerProps}>
      <div
        ref={containerRef}
        onClick={onClickOptions}
        data-testid={props['data-testid']}
        className={classes.actionOptionsHandleContainer}
      >
        {pulse && !anchor ? <div className={classes.actionOptionsPulse} /> : null}
        <div className={classes.actionOptionsHandle}>
          <div className={classes.actionOptionsContainer}>
            <PlusIcon />
          </div>
        </div>
      </div>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={onCloseOptions}
        transformOrigin={
          layoutDir === 'TB'
            ? { horizontal: 'center', vertical: 'top' }
            : { horizontal: 'left', vertical: 'center' }
        }
        anchorOrigin={
          layoutDir === 'TB'
            ? { horizontal: 'center', vertical: 'bottom' }
            : { horizontal: 'right', vertical: 'center' }
        }
      >
        {typeof children === 'function' ? children({ onCloseOptions }) : childrenWithProps}
      </Menu>
    </Container>
  );
};
